import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import i18n from '../i18n';
import { supabase, supabaseEnabled } from './supabase';
import { ecosystemSeed, heroCtaSeed, socialSeed, heroStatDefaults, type EcosystemSeed } from '../data/ecosystem';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface CmsLink {
  id?: string;
  slug: string;
  url: string;
  category: string;
  icon?: string | null;
  accent?: string | null;
  title: Record<string, string>;
  description: Record<string, string>;
  sort_order: number;
  is_published: boolean;
  /** button style for category='hero_cta' rows: 'primary' | 'secondary' */
  variant?: string | null;
}

export interface CmsContentRow {
  key: string;
  locale: string;
  value: string;
}

interface CmsState {
  links: CmsLink[];
  settings: Record<string, unknown>;
  loaded: boolean;
  /** true only after a successful CMS fetch — then CMS links are authoritative */
  available: boolean;
}

const CmsContext = createContext<CmsState>({ links: [], settings: {}, loaded: false, available: false });

// ---------------------------------------------------------------------------
// i18n content overrides — layer admin edits on top of the static JSON so every
// existing t() call picks them up without touching a single component.
// ---------------------------------------------------------------------------
function nest(dotted: string, value: string): Record<string, unknown> {
  const parts = dotted.split('.');
  const root: Record<string, unknown> = {};
  let node = root;
  parts.forEach((part, i) => {
    if (i === parts.length - 1) node[part] = value;
    else node = node[part] = (node[part] as Record<string, unknown>) ?? {};
  });
  return root;
}

function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>) {
  for (const [k, v] of Object.entries(source)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      target[k] = deepMerge((target[k] as Record<string, unknown>) ?? {}, v as Record<string, unknown>);
    } else {
      target[k] = v;
    }
  }
  return target;
}

export function applyContentOverrides(rows: CmsContentRow[]) {
  const byLocale = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    if (!row.value) continue;
    const acc = byLocale.get(row.locale) ?? {};
    deepMerge(acc, nest(row.key, row.value));
    byLocale.set(row.locale, acc);
  }
  for (const [locale, bundle] of byLocale) {
    // deep + overwrite so admin edits win over the shipped translation
    i18n.addResourceBundle(locale, 'translation', bundle, true, true);
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
interface PreviewData {
  links?: CmsLink[];
  settings?: Record<string, unknown>;
}

export function CmsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CmsState>({ links: [], settings: {}, loaded: false, available: false });
  const [preview, setPreview] = useState<PreviewData | null>(null);

  useEffect(() => {
    if (!supabaseEnabled || !supabase) {
      setState((s) => ({ ...s, loaded: true }));
      return;
    }
    let cancelled = false;
    (async () => {
      const [linksRes, contentRes, settingsRes] = await Promise.all([
        supabase.from('site_links').select('*').eq('is_published', true).order('sort_order'),
        supabase.from('site_content').select('key,locale,value'),
        supabase.from('site_settings').select('key,value'),
      ]);
      /* v8 ignore next -- unmount race: cancelled flips only if the component
         unmounts mid-load, which the suite does not exercise deterministically */
      if (cancelled) return;
      if (contentRes.data) applyContentOverrides(contentRes.data as CmsContentRow[]);
      const settings: Record<string, unknown> = {};
      for (const row of (settingsRes.data ?? []) as { key: string; value: unknown }[]) {
        settings[row.key] = row.value;
      }
      setState({
        links: (linksRes.data as CmsLink[]) ?? [],
        settings,
        loaded: true,
        available: !linksRes.error, // authoritative only if the links query succeeded
      });
    })().catch(() => setState((s) => ({ ...s, loaded: true }))); // network failure → available stays false → static seed
    return () => {
      cancelled = true;
    };
  }, []);

  // Live-preview: when loaded inside the admin's ?preview iframe, apply draft
  // edits pushed via postMessage without saving (WordPress-Customizer style).
  useEffect(() => {
    /* v8 ignore next -- jsdom/browser always define window */
    if (typeof window === 'undefined') return;
    if (!new URLSearchParams(window.location.search).has('preview')) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return; // same-origin only
      const d = e.data as { __cmsPreview?: boolean; links?: CmsLink[]; settings?: Record<string, unknown>; content?: CmsContentRow[]; previewLocale?: string };
      if (!d || !d.__cmsPreview) return;
      if (d.previewLocale) i18n.changeLanguage(d.previewLocale);
      if (d.content) applyContentOverrides(d.content);
      if (d.links || d.settings) setPreview({ links: d.links, settings: d.settings });
    };
    window.addEventListener('message', onMessage);
    window.parent?.postMessage({ __cmsPreviewReady: true }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const value = useMemo<CmsState>(() => ({
    links: preview?.links ?? state.links,
    settings: preview?.settings ? { ...state.settings, ...preview.settings } : state.settings,
    loaded: state.loaded,
    // only a links draft makes the CMS authoritative; a settings-only draft
    // (sections/stats preview) leaves link resolution as-is (seed or fetched).
    available: state.available || preview?.links != null,
  }), [state, preview]);

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms() {
  return useContext(CmsContext);
}

// ---------------------------------------------------------------------------
// Ecosystem links: merge static seed with CMS rows for the public grid.
// ---------------------------------------------------------------------------
export interface ResolvedLink {
  slug: string;
  url: string;
  monogram: string;
  accent: string;
  /** override title/description maps from the CMS (may be empty) */
  title: Record<string, string>;
  description: Record<string, string>;
}

function seedToResolved(s: EcosystemSeed): ResolvedLink {
  return { slug: s.slug, url: s.url, monogram: s.monogram, accent: s.accent, title: {}, description: {} };
}

export function useEcosystemLinks(): ResolvedLink[] {
  const { links, available } = useCms();
  return useMemo(() => {
    // Until the CMS confirms a successful load, show the static seed (SEO
    // baseline + resilience if Supabase is unreachable).
    if (!available) return ecosystemSeed.map(seedToResolved);

    // CMS is authoritative once loaded — honours additions, edits, reordering
    // and deletions. Seed only enriches styling (monogram/accent) per slug.
    const seedBySlug = new Map(ecosystemSeed.map((s) => [s.slug, s]));
    return links.filter((l) => (l.category ?? 'subdomain') === 'subdomain').map((l) => {
      const seed = seedBySlug.get(l.slug);
      return {
        slug: l.slug,
        url: l.url,
        monogram: l.icon || seed?.monogram || l.slug.slice(0, 4).toUpperCase(),
        accent: l.accent || seed?.accent || '#1d4ed8',
        title: l.title ?? {},
        description: l.description ?? {},
      };
    });
  }, [links, available]);
}

// ---------------------------------------------------------------------------
// Hero CTA buttons (category='hero_cta'). Text resolves from the title override,
// then the i18n key `hero.<slug>` (see Ecosystem/App), so localization holds.
// ---------------------------------------------------------------------------
export interface ResolvedCta {
  slug: string;
  url: string;
  variant: string;
  title: Record<string, string>;
}

export function useHeroCtas(): ResolvedCta[] {
  const { links, available } = useCms();
  return useMemo(() => {
    if (!available) return heroCtaSeed.map((c) => ({ slug: c.slug, url: c.url, variant: c.variant, title: {} }));
    return links
      .filter((l) => l.category === 'hero_cta')
      .map((l) => ({ slug: l.slug, url: l.url, variant: l.variant || 'secondary', title: l.title ?? {} }));
  }, [links, available]);
}

// ---------------------------------------------------------------------------
// Section visibility — settings.sections is a { [sectionId]: boolean } map;
// a missing entry defaults to visible.
// ---------------------------------------------------------------------------
export function useSectionVisibility(): (id: string) => boolean {
  const { settings } = useCms();
  return useMemo(() => {
    const map = (settings.sections as Record<string, boolean> | undefined) ?? {};
    return (id: string) => map[id] !== false;
  }, [settings]);
}

// ---------------------------------------------------------------------------
// Social links (category='social') + hero trust-stat values (settings.hero_stats).
// ---------------------------------------------------------------------------
export interface ResolvedSocial {
  slug: string;
  url: string;
  icon: string;
  label: string;
}

export function useSocialLinks(): ResolvedSocial[] {
  const { links, available } = useCms();
  return useMemo(() => {
    if (!available) return socialSeed.map((s) => ({ slug: s.slug, url: s.url, icon: s.icon, label: s.label }));
    return links
      .filter((l) => l.category === 'social')
      .map((l) => ({ slug: l.slug, url: l.url, icon: l.icon || l.slug, label: l.title.en || l.slug }));
  }, [links, available]);
}

export function useHeroStat(): (labelKey: string) => string {
  const { settings } = useCms();
  return useMemo(() => {
    const map = (settings.hero_stats as Record<string, string> | undefined) ?? {};
    return (labelKey: string) => map[labelKey] || heroStatDefaults[labelKey] || '';
  }, [settings]);
}
